<!--
  Dynamic Table Component Template
  ===============================
  
  Advanced table template for displaying form entries with inline filtering and sorting.
  This template provides a comprehensive desktop interface that dynamically renders
  columns based on form metadata and supports real-time filtering operations.
  
  Key Features:
  - Dynamic column rendering with configurable types
  - Inline filtering with multiple operation types
  - Column sorting with visual indicators
  - Form type-specific dropdown filtering
  - Date and number field comparison filtering
  - Status field rendering with color-coded badges
  - Responsive table design with horizontal scrolling
  
  Column Types Supported:
  - text: Standard text input with equals/contains operations
  - date: Date input with comparison operations and formatting
  - status: Color-coded badge display with predefined color mapping
  - action: Clickable rows for navigation
  - number: Numeric input with comparison operations
  
  Filter Operations:
  - Text fields: Equals (=), Contains
  - Date/Number fields: Equals, Contains, Greater Than (>=), Less Than (<=)
  - Form Type: Dropdown selection with automatic API format conversion
  
  @author MacSafety Development Team
  @version 1.0
-->

<ion-card class="no-card-style" >
  <ion-card-content>
    <div class="table-scroll-container">
      <table class="data-table">
        <!-- Table Header Section -->
        <thead>
          <tr>
            <th *ngFor="let column of columns" 
                [style.width]="column.width || 'auto'">
              
              <!-- Column Header Container -->
              <div class="column-header">
                <div class="column-content">
                  <span class="column-label">{{ column.label }}</span>
                  
                  <!-- Sort and Filter Icons Container -->
                  <div class="column-actions">
                    <!-- Sort Icon - Clickable for sortable columns -->
                    <ion-icon 
                             [name]="getSortIcon(column.key)"
                             class="sort-icon"
                             (click)="column.sortable && onSort(column.key)">
                    </ion-icon>
                    
                    <!-- Filter Icon - Toggles filter input row -->
                    <ion-icon 
                        name="filter-outline" 
                        class="filter-icon"
                        (click)="onFilterClick(column.key); $event.stopPropagation()">
                    </ion-icon>
                  </div>
                </div>

                <!-- Filter Input Row - Expandable filter interface -->
                <div *ngIf="shouldShowFilterInput(column.key)" class="filter-input-row">
                  <div class="filter-input-container">
                    <!-- Operation Selector - For text, date, and number columns -->
                    <ion-text 
                      *ngIf="column.key!=='formType'"
                      class="operation-selector"
                      (click)="onOperationSelect(column.key)">
                      {{ getOperationText(column.key) }}
                      <ion-icon name="chevron-down-outline" size="small"></ion-icon>
                    </ion-text>

                    <!-- Form Type Dropdown - Special handling for form type column -->
                    <ion-select *ngIf="column.key==='formType'"
                    [value]="filterValues[column.key] || ''"
                    (ionChange)="onFilterApplyFormType($event, column.key)"
                    placeholder="Select form type"
                    interface="popover">
                    <ion-select-option *ngFor="let type of formTypes" [value]="type.formType">
                      {{ type.title }}
                    </ion-select-option>
                  </ion-select>

                    <!-- Filter Input Field - For text, date, and number columns -->
                    <ion-input *ngIf="column.key!=='formType'"
                      [value]="filterValues[column.key] || ''"
                      (ionInput)="onFilterInput($event, column.key)"
                      [placeholder]="getFilterPlaceholder(column.type)"
                      [type]="getFilterInputType(column.type)" 
                      size="small">
                    </ion-input>
             
                    <!-- Filter Action Buttons -->
                    <div class="filter-buttons">
                      <!-- Apply Filter Button - For text, date, and number columns -->
                      <ion-button *ngIf="column.key!=='formType'"
                        fill="clear" 
                        size="small"
                        (click)="onFilterApply(column.key)">
                        <ion-icon name="checkmark-outline" slot="icon-only"></ion-icon>
                      </ion-button>
                      <!-- Clear Filter Button - Available for all column types -->
                      <ion-button 
                        fill="clear" 
                        size="small"
                        (click)="onFilterClear(column.key)">
                        <ion-icon name="close-outline" slot="icon-only"></ion-icon>
                      </ion-button>
                    </div>
                  </div>
                </div>
              </div>
            </th>
          </tr>
        </thead>

        <!-- Table Body Section -->
        <tbody>
          <!-- Data Rows - Clickable for navigation -->
          <tr *ngFor="let row of data" (click)="onAction(row)" class="data-row">
            <td *ngFor="let column of columns" 
                [style.width]="column.width || 'auto'">
              
              <!-- Text Column Rendering -->
              <ng-container *ngIf="column.type === 'text'">
                {{ row.formEntryData[column.key] }}
              </ng-container>

              <!-- Date Column Rendering - With conditional formatting -->
              <ng-container *ngIf="column.type === 'date'">
                {{ row.formEntryData[column.key] | date : (column.key === 'submissionDate' ? "MMM d, y, h:mm a" : "MMM d, y") }}
              </ng-container>

              <!-- Status Column Rendering - With color-coded badges -->
              <ng-container *ngIf="column.type === 'status'">
                <ion-badge [color]="getStatusColor(row.formEntryData[column.key])">
                  {{ row.formEntryData[column.key] }}
                </ion-badge>
              </ng-container>
            </td>
          </tr>

          <!-- Empty State Row - Shown when no data is available -->
          <tr *ngIf="!data?.length && !loadingState.isLoading" class="empty-row">
            <td colspan="100%">
              <ion-text color="medium">No entries found</ion-text>
            </td>
          </tr>
        </tbody>
      </table>
          <!-- Loading State Card - Shown when data is being loaded -->
    <ion-card *ngIf="loadingState.isLoading" class="state-card">
      <ion-card-content class="ion-text-center">
        <ion-spinner name="circular"></ion-spinner>
        <p>{{ loadingState.message }}</p>
      </ion-card-content>
    </ion-card>
    </div>
  </ion-card-content>
</ion-card>  